<script>
import Layout from "../../layouts/main";
import EasyDataTable from "vue3-easy-data-table";
import "vue3-easy-data-table/dist/style.css";
import axios from "axios";
import serverTableMixin from "@/mixins/serverTable";
import AppIcon from "@/components/shared/AppIcon.vue";
import TableFooter from "@/components/shared/TableFooter.vue";

import { formatDate } from "@/helpers/dateFormatter";

export default {
  name: "StaffViewLog",
  mixins: [serverTableMixin],
  components: {
    Layout,
    EasyDataTable,
    AppIcon,
    TableFooter,
  },
  data() {
    return {
      searchValueTable: "",
      selectFilterSort: null,
      // the API can only order logs by date
      sortFieldMap: { logdate: "created_on" },
      headers: [
        { text: "Staff Name", value: "staffname" },
        { text: "Date & Time", value: "logdate", sortable: true },
        { text: "Activity", value: "activity" },
      ],
      items: [],
      arrItem: [],
    };
  },
  created() {
    // the mixin has already read page / search / sort from the URL
    this.loadTable();
  },
  methods: {
    initials(name) {
      return (
        String(name || "")
          .split(/\s+/)
          .filter(Boolean)
          .slice(0, 2)
          .map((w) => w.charAt(0).toUpperCase())
          .join("") || "?"
      );
    },
    // footer controls -> the server-side table options (the mixin reloads on change)
    onPageChange(page) {
      this.serverOptions = { ...this.serverOptions, page };
    },
    onRowsChange(rowsPerPage) {
      this.serverOptions = { ...this.serverOptions, rowsPerPage, page: 1 };
    },
    // clear the search box and reload the table (same behaviour as the Staff page)
    clearSearch() {
      this.searchValueTable = "";
      clearTimeout(this.searchTimer);
      this.resetAndLoad();
    },
    requestTable(params) {
      return axios.get("/accounts/get_logs/", { params });
    },
    applyTable(data) {
      this.arrItem = (data.results || []).map((item) => {
        const formattedDate = formatDate(item.created_on || item.created_at);
        return {
          staffname:
            item.user_name || (item.user && item.user.username) || "Staff",
          logdate: formattedDate,
          dateLog: formattedDate,
          activity: item.log,
        };
      });
    },
  },
};
</script>

<template>
  <Layout>
    <section class="slog">
      <!-- Heading (left) + search (right), on one line -->
      <header class="ecu-page-head slog__head">
        <div>
          <h1>View Log</h1>
          <p class="ecu-page-head__sub">Activity recorded by staff members across applications.</p>
        </div>

        <div class="slog__tools">
          <div class="ecu-field ecu-field--search">
            <AppIcon name="search" :size="18" />
            <input
              type="text"
              v-model="searchValueTable"
              @input="onSearchInput"
              placeholder="Search logs..."
              aria-label="Search logs"
              autocomplete="off"
            />
            <button
              v-if="searchValueTable"
              type="button"
              class="slog__clear"
              aria-label="Clear search"
              @click="clearSearch"
            >
              <AppIcon name="x" :size="14" :stroke="2.4" />
            </button>
          </div>
        </div>
      </header>

      <div class="ecu-card">
        <div class="ecu-table-wrap">
          <EasyDataTable
            header-text-direction="center"
            :headers="headers"
            :items="arrItem"
            v-model:server-options="serverOptions"
            :server-items-length="totalItems"
            :loading="tableLoading"
            :rows-items="[10, 20, 50, 100]"
            empty-message="No activity recorded yet"
            theme-color="#f97316"
            hide-footer
            table-class-name="table-custom-style"
          >
            <template #item-staffname="{ staffname }">
              <span class="slog__person">
                <span class="slog__avatar">{{ initials(staffname) }}</span>
                {{ staffname }}
              </span>
            </template>
            <template #item-logdate="{ logdate, dateLog }">
              <span class="ecu-date">
                <AppIcon name="clock" :size="16" />
                {{ logdate || dateLog }}
              </span>
            </template>
            <template #item-activity="{ activity }">
              <span class="slog__activity">{{ activity }}</span>
            </template>
          </EasyDataTable>
        </div>

        <TableFooter
          :page="serverOptions.page"
          :rows-per-page="serverOptions.rowsPerPage"
          :total="totalItems"
          :loading="tableLoading"
          @update:page="onPageChange"
          @update:rowsPerPage="onRowsChange"
        />
      </div>
    </section>
  </Layout>
</template>

<style scoped>
.slog {
  display: flex;
  flex-direction: column;
  gap: 22px;
}
.slog .ecu-page-head {
  margin-bottom: 0;
}
/* heading on the left, search on the right, vertically aligned */
.slog__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}
.slog__tools {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-left: auto;
}
.slog__tools .ecu-field--search {
  position: relative;
  min-width: 260px;
}
.slog__tools .ecu-field--search input {
  padding-right: 40px;
}
/* clear (X) button inside the search box */
.slog__clear {
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #5f6579;
  cursor: pointer;
}
.slog__clear:hover {
  color: #e84d00;
}
.slog .ecu-card {
  padding: 22px 22px 18px;
}
.slog__person {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  color: var(--ecu-navy, #0b1f4b);
  white-space: nowrap;
}
.slog__avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: var(--ecu-fs-small);
  font-weight: 800;
  color: var(--ecu-orange-dark, #e84d00);
  background: var(--ecu-orange-soft, #fff1e6);
}
.slog__activity {
  display: inline-block;
  line-height: 1.45;
  font-weight: 500;
  color: var(--ecu-ink, #1d2a4d);
  white-space: normal;
}
@media (max-width: 767px) {
  .slog .ecu-card {
    padding: 16px 14px 14px;
  }
  .slog__tools {
    width: 100%;
    margin-left: 0;
  }
  .slog__tools .ecu-field--search {
    flex: 1 1 100%;
    min-width: 0;
  }
}
</style>